import { useCallback } from 'react'

import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { Box, Button, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { type ChartOptions } from 'chart.js'
import { format, parseISO } from 'date-fns'
import { Line } from 'react-chartjs-2'

import { CardTitle } from 'components/common/controls'
import { DATE_DAY_MONTH_FORMAT, DATE_FORMAT } from 'constants/index'
import {
  DriverLocationTrackingReportInputDtoInput,
  DriverUserTypeFilter,
  LiveTrackingDataPoint,
} from 'generated/graphql'

const USER_TYPE_LABELS: Record<DriverUserTypeFilter, string> = {
  [DriverUserTypeFilter.AllJobs]: 'All Jobs',
  [DriverUserTypeFilter.AppUsers]: 'App Users',
  [DriverUserTypeFilter.NonAppUsers]: 'Non-app Users',
}

interface IProps {
  dataPoints: LiveTrackingDataPoint[]
  filter: DriverLocationTrackingReportInputDtoInput
}

const LiveTrackingChart = ({ dataPoints, filter }: IProps) => {
  const theme = useTheme()
  const countColor = theme.palette.primary.main
  const percentageColor = theme.palette.info.main

  const chartData = dataPoints.map((point) => ({
    ...point,
    dateLabel: format(parseISO(point.date as unknown as string), DATE_FORMAT),
    dateDisplay: format(parseISO(point.date as unknown as string), DATE_DAY_MONTH_FORMAT),
  }))

  const userTypeLabel = USER_TYPE_LABELS[filter.userType ?? DriverUserTypeFilter.AllJobs]
  const startLabel = filter.start ? format(new Date(filter.start), DATE_FORMAT) : '—'
  const endLabel = filter.end ? format(new Date(filter.end), DATE_FORMAT) : '—'

  const earliestDataDate =
    dataPoints.length > 0 ? parseISO(dataPoints[0].date as unknown as string) : null
  const filterStart = filter.start ? new Date(filter.start) : null
  const dataStartsLater =
    earliestDataDate !== null &&
    filterStart !== null &&
    earliestDataDate.getTime() - filterStart.getTime() > 24 * 60 * 60 * 1000

  const handleExportCsv = useCallback(() => {
    const header = 'Date,Jobs with live tracking (count),Jobs with live tracking (%),Total jobs'
    const rows = dataPoints.map(
      (p) =>
        `${format(parseISO(p.date as unknown as string), DATE_FORMAT)},${p.jobsWithLiveTracking},${
          p.percentage
        },${p.totalJobs}`,
    )
    const csv = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `driver-location-tracking-${startLabel}-${endLabel}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }, [dataPoints, startLabel, endLabel])

  const data = {
    labels: chartData.map((p) => p.dateDisplay),
    datasets: [
      {
        label: 'Jobs with live tracking (count)',
        data: chartData.map((p) => p.jobsWithLiveTracking),
        yAxisID: 'count',
        borderColor: countColor,
        backgroundColor: countColor,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0,
      },
      {
        label: 'Jobs with live tracking (%)',
        data: chartData.map((p) => p.percentage),
        yAxisID: 'percentage',
        borderColor: percentageColor,
        backgroundColor: percentageColor,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0,
      },
    ],
  }

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        backgroundColor: '#fff',
        titleColor: '#000',
        bodyColor: '#000',
        footerColor: '#000',
        borderColor: 'rgba(0,0,0,0.15)',
        borderWidth: 1,
        callbacks: {
          title: (items) => chartData[items[0].dataIndex]?.dateLabel ?? '',
          label: (item) => {
            const val = item.raw as number
            return item.datasetIndex === 0
              ? `Jobs with live tracking (count): ${val.toLocaleString()}`
              : `Jobs with live tracking (%): ${val}%`
          },
          footer: (items) =>
            `Total jobs: ${(chartData[items[0].dataIndex]?.totalJobs ?? 0).toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(0,0,0,0.08)' },
        ticks: { font: { size: 12 } },
        title: { display: true, text: 'Date', font: { size: 12 } },
      },
      count: {
        type: 'linear',
        position: 'left',
        grid: { color: 'rgba(0,0,0,0.08)' },
        ticks: { color: countColor, font: { size: 12 } },
        title: { display: true, text: 'Count', color: countColor, font: { size: 12 } },
      },
      percentage: {
        type: 'linear',
        position: 'right',
        min: 0,
        max: 100,
        grid: { drawOnChartArea: false },
        ticks: {
          color: percentageColor,
          font: { size: 12 },
          callback: (v) => `${v}%`,
        },
        title: {
          display: true,
          text: 'Percentage (%)',
          color: percentageColor,
          font: { size: 12 },
        },
      },
    },
  }

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='flex-start' mb={1}>
        <Box>
          <CardTitle marginBottom={0}>Live Tracking Adoption</CardTitle>
          <Typography variant='body2' color='textSecondary'>
            Daily timeseries showing the count and percentage of jobs with live tracking enabled.
          </Typography>
        </Box>

        <Button
          variant='outlined'
          startIcon={<FileDownloadIcon />}
          onClick={handleExportCsv}
          disabled={dataPoints.length === 0}>
          Export CSV
        </Button>
      </Box>

      <Typography variant='body2' gutterBottom>
        <strong>Filter:</strong> {userTypeLabel}
      </Typography>

      {dataPoints.length === 0 ? (
        <Box py={6} textAlign='center'>
          <Typography variant='body1' color='textSecondary'>
            No data available for the selected criteria.
          </Typography>
        </Box>
      ) : (
        <>
          <Box height={420}>
            <Line data={data} options={options} />
          </Box>

          {dataStartsLater && (
            <Typography variant='caption' color='warning.main' display='block' mt={1}>
              No data available before {format(earliestDataDate!, DATE_FORMAT)}. Chart starts from
              earliest available data point.
            </Typography>
          )}
          <Typography variant='caption' color='textSecondary' display='block' mt={0.5}>
            Showing daily data from {startLabel} to {endLabel}.
          </Typography>
        </>
      )}
    </Box>
  )
}

export default LiveTrackingChart
